/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { mono, monoFont } from './monolith.tokens'

export interface MonolithSelectOptionType {
	value: string
	/** 진하게 — 그 언어 표기처럼 주 이름 */
	label: string
	/** 옅게 — 영어 이름처럼 보조 이름. label 과 같으면 생략 */
	hint?: string
}

interface PropsType {
	id: string
	value: string
	options: MonolithSelectOptionType[]
	onChange: (value: string) => void
	/** 바깥 `.mnLabel` 의 id. 버튼·목록의 이름이 된다 */
	labelledBy?: string
	className?: string
}

/**
 * 셀렉트 — Monolith(2안). OS 기본 메뉴 대신 사이트 문법의 목록 패널을 띄운다(2026-10-28 사용자 요청).
 *
 * 닫힌 모양은 폼 밑줄 입력(`monoForm` 의 `.mnInput`) + Lucide 셰브론 그대로 — Contact·Payment 의
 * 네이티브 셀렉트와 같은 높이·선·글꼴이다. 펼치면 surface 패널(헤어라인, radius 2)에 행마다
 * 주 이름(ink) + 보조 이름(faint), 현재 값에 체크.
 *
 * 키보드는 WAI-ARIA listbox 패턴: ↑↓ Home End 로 이동, Enter·Space 선택, Esc·Tab 닫기.
 * 밖을 누르면 닫힌다. 첫 사용 = 웹 매뉴얼 언어 선택(ManualReader).
 */
export const MonolithSelect = (props: PropsType) => {
	const { id, value, options, onChange } = props
	const [isOpen, setIsOpen] = React.useState(false)
	const [activeIdx, setActiveIdx] = React.useState(0)

	const rootRef = React.useRef<HTMLDivElement>(null)
	const buttonRef = React.useRef<HTMLButtonElement>(null)
	const listRef = React.useRef<HTMLUListElement>(null)

	const selectedIdx = Math.max(
		0,
		options.findIndex(anOption => anOption.value === value),
	)
	const selected = options[selectedIdx]
	const listId = `${id}-list`
	const optionId = (idx: number) => `${id}-opt-${idx}`

	const open = () => {
		setActiveIdx(selectedIdx)
		setIsOpen(true)
	}

	const close = (refocus: boolean) => {
		setIsOpen(false)
		if (refocus) buttonRef.current?.focus()
	}

	const choose = (idx: number) => {
		close(true)
		if (options[idx] !== undefined && options[idx].value !== value) {
			onChange(options[idx].value)
		}
	}

	// 열리면 목록으로 포커스, 밖을 누르면 닫힌다
	React.useEffect(() => {
		if (!isOpen) return
		listRef.current?.focus()
		const onDown = (event: MouseEvent) => {
			if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
				close(false)
			}
		}
		document.addEventListener('mousedown', onDown)
		return () => document.removeEventListener('mousedown', onDown)
	}, [isOpen])

	// 활성 행이 패널 밖이면 패널만 스크롤
	React.useEffect(() => {
		if (!isOpen) return
		document.getElementById(optionId(activeIdx))?.scrollIntoView({ block: 'nearest' })
	}, [isOpen, activeIdx])

	const onButtonKey = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			open()
		}
	}

	const onListKey = (event: React.KeyboardEvent<HTMLUListElement>) => {
		const last = options.length - 1
		const move: { [key: string]: () => number } = {
			ArrowDown: () => Math.min(activeIdx + 1, last),
			ArrowUp: () => Math.max(activeIdx - 1, 0),
			Home: () => 0,
			End: () => last,
		}
		if (move[event.key] !== undefined) {
			event.preventDefault()
			setActiveIdx(move[event.key]())
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			choose(activeIdx)
		} else if (event.key === 'Escape') {
			event.preventDefault()
			close(true)
		} else if (event.key === 'Tab') {
			close(false)
		}
	}

	const renderName = (anOption: MonolithSelectOptionType) => (
		<>
			<span className="msLabel">{anOption.label}</span>
			{anOption.hint !== undefined && anOption.hint !== anOption.label && (
				<span className="msHint">{anOption.hint}</span>
			)}
		</>
	)

	return (
		<div css={style} className={props.className} ref={rootRef}>
			<button
				ref={buttonRef}
				id={id}
				type="button"
				className={`msTrigger ${isOpen ? 'open' : ''}`}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={listId}
				aria-labelledby={
					props.labelledBy === undefined ? undefined : `${props.labelledBy} ${id}`
				}
				onClick={() => (isOpen ? close(true) : open())}
				onKeyDown={onButtonKey}
			>
				<span className="msValue">{selected !== undefined && renderName(selected)}</span>
				<svg
					className="msChevron"
					xmlns="http://www.w3.org/2000/svg"
					width={18}
					height={18}
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth={1}
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<path d="m6 9 6 6 6-6" />
				</svg>
			</button>

			{isOpen && (
				<ul
					ref={listRef}
					id={listId}
					className="msList"
					role="listbox"
					tabIndex={-1}
					aria-labelledby={props.labelledBy}
					aria-activedescendant={optionId(activeIdx)}
					onKeyDown={onListKey}
				>
					{options.map((anOption, idx) => {
						const isSelected = idx === selectedIdx
						return (
							<li
								key={anOption.value}
								id={optionId(idx)}
								role="option"
								aria-selected={isSelected}
								className={`msOption ${idx === activeIdx ? 'active' : ''} ${
									isSelected ? 'selected' : ''
								}`}
								onMouseEnter={() => setActiveIdx(idx)}
								onClick={() => choose(idx)}
							>
								<span className="msName">{renderName(anOption)}</span>
								{isSelected && (
									<svg
										className="msCheck"
										xmlns="http://www.w3.org/2000/svg"
										width={16}
										height={16}
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth={1.5}
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
									>
										<path d="M20 6 9 17l-5-5" />
									</svg>
								)}
							</li>
						)
					})}
				</ul>
			)}
		</div>
	)
}

const style = css({
	position: 'relative',
	width: '100%',
	fontFamily: monoFont.sans,

	// 닫힌 모양 = monoForm `.mnInput` 과 같은 밑줄 입력
	'.msTrigger': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		width: '100%',
		height: 46,
		padding: 0,
		border: 0,
		borderBottom: `1px solid ${mono.lineInput}`,
		borderRadius: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 16,
		fontWeight: 300,
		color: mono.ink,
		textAlign: 'left',
		cursor: 'pointer',
		outline: 'none',

		'&:hover, &:focus-visible, &.open': {
			borderBottomColor: mono.ink,
		},
		'.msChevron': {
			flexShrink: 0,
			color: mono.faint,
			transition: 'transform 150ms ease-out',
		},
		'&.open .msChevron': {
			transform: 'rotate(180deg)',
		},
	},

	'.msValue, .msName': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 10,
		minWidth: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
	},
	'.msLabel': {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'.msHint': {
		fontSize: 13,
		color: mono.faint,
	},

	// 펼친 목록 — surface 패널, 헤어라인
	'.msList': {
		position: 'absolute',
		top: 'calc(100% + 6px)',
		left: 0,
		right: 0,
		zIndex: 10,
		maxHeight: 320,
		overflowY: 'auto',
		margin: 0,
		padding: '6px 0',
		listStyle: 'none',
		background: mono.surface,
		border: `1px solid ${mono.line}`,
		borderRadius: 2,
		boxShadow: '0 12px 32px rgba(22, 24, 27, 0.08)',
		outline: 'none',
	},
	'.msOption': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 12,
		padding: '11px 16px',
		fontSize: 15,
		fontWeight: 400,
		color: mono.dim,
		cursor: 'pointer',

		'&.active': {
			background: mono.bg,
			color: mono.ink,
		},
		'&.selected': {
			color: mono.ink,
			fontWeight: 500,
		},
		'.msCheck': {
			flexShrink: 0,
			color: mono.ink,
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.msChevron': { transition: 'none' },
	},
})
